import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { Pass } from "three/examples/jsm/postprocessing/Pass";
import { Mesh, Scene, WebGLRenderer } from "three";
import { frameProfiler } from "./FrameProfiler";

/**
 * Engine tarafındaki profiling/log entegrasyonu.
 * Engine.animate içinden kısa fonksiyon çağrılarıyla kullanılır;
 * profiler kapalıyken tüm fonksiyonlar no-op (overhead 1-2 ns).
 *
 * Parametrik: setConfig ile hangi ölçümlerin alınacağı açılıp kapatılabilir.
 */

export interface EngineProfilingConfig {
  /** Frame fazlarını (entities.animate, renderScene, vb.) ölç. */
  tracePhases: boolean;
  /** EffectComposer passelerini tek tek ölç. */
  tracePasses: boolean;
  /** Entity.animate / beforeRender sürelerini constructor.name ile ölç. */
  perEntity: boolean;
  /** engine/scene yapısal sayaçları (entities.count, scene.meshes, ...). */
  gaugeStructural: boolean;
  /** WebGL renderer sayaçları (gl.calls, gl.triangles, ...). */
  gaugeGl: boolean;
}

const DEFAULT_CONFIG: EngineProfilingConfig = {
  tracePhases: true,
  tracePasses: true,
  perEntity: true,
  gaugeStructural: true,
  gaugeGl: true,
};

let _config: EngineProfilingConfig = { ...DEFAULT_CONFIG };

type InstrumentedPass = Pass & {
  __fpInstrumented?: boolean;
  render: (...args: unknown[]) => void;
};

export const engineProfiling = {
  get enabled(): boolean {
    return frameProfiler.enabled;
  },

  setConfig(next: Partial<EngineProfilingConfig>): void {
    _config = { ..._config, ...next };
  },

  getConfig(): EngineProfilingConfig {
    return { ..._config };
  },

  resetConfig(): void {
    _config = { ...DEFAULT_CONFIG };
  },

  /** Bir faz süresini ölçerek fonksiyonu çalıştır; profiler/faz kapalıysa doğrudan çağırır. */
  phase<T>(label: string, fn: () => T): T {
    if (!frameProfiler.enabled || !_config.tracePhases) return fn();
    frameProfiler.begin(label);
    try {
      return fn();
    } finally {
      frameProfiler.end(label);
    }
  },

  /**
   * EffectComposer'daki passlerin render() metodunu idempotent sekilde sarmalar.
   * Profiler kapaliyken wrap orig'e dusuyor, disabled-yolu cok ucuz.
   */
  instrumentComposer(composer?: EffectComposer): void {
    if (!composer || !_config.tracePasses) return;
    for (const pass of composer.passes) {
      const p = pass as InstrumentedPass;
      if (p.__fpInstrumented) continue;
      const orig = p.render.bind(p);
      const label = "pass:" + (p.constructor?.name || "Unknown");
      p.render = function (...args: unknown[]) {
        if (!frameProfiler.enabled) return orig(...args);
        const t0 = performance.now();
        try {
          return orig(...args);
        } finally {
          frameProfiler.addSample(label, performance.now() - t0);
        }
      } as typeof p.render;
      p.__fpInstrumented = true;
    }
  },

  /** Yapisal sayilari gauge'lere aktar (entities.count, composer.passes, scene.meshes, ...). */
  captureStructuralGauges(opts: {
    entitiesCount: number;
    passesCount: number;
    renderTargetsCount: number;
    rafScheduledTotal: number;
    scene?: Scene;
  }): void {
    if (!frameProfiler.enabled || !_config.gaugeStructural) return;
    frameProfiler.setGauge("entities.count", opts.entitiesCount);
    frameProfiler.setGauge("composer.passes", opts.passesCount);
    frameProfiler.setGauge("renderTargets", opts.renderTargetsCount);
    frameProfiler.setGauge("raf.scheduled.total", opts.rafScheduledTotal);
    if (opts.scene) {
      let objs = 0;
      let meshes = 0;
      opts.scene.traverse((o) => {
        objs++;
        if ((o as Mesh).isMesh) meshes++;
      });
      frameProfiler.setGauge("scene.objects", objs);
      frameProfiler.setGauge("scene.meshes", meshes);
    }
  },

  /** WebGLRenderer info sayaclarini gauge'lere aktar. */
  captureGlGauges(renderer: unknown): void {
    if (!frameProfiler.enabled || !_config.gaugeGl) return;
    if (!(renderer instanceof WebGLRenderer)) return;
    frameProfiler.setGauge("gl.calls", renderer.info.render.calls);
    frameProfiler.setGauge("gl.triangles", renderer.info.render.triangles);
    frameProfiler.setGauge("gl.geometries", renderer.info.memory.geometries);
    frameProfiler.setGauge("gl.textures", renderer.info.memory.textures);
    frameProfiler.setGauge("gl.programs", renderer.info.programs?.length ?? 0);
  },
};

/**
 * Method decorator: sardigi metodu engineProfiling.phase ile olcer.
 * Tek toplam kaydi phases tablosuna yazar. Profiler kapaliyken no-op.
 *
 * Kullanim:
 *   @profile("renderScene")
 *   private _renderScene(): void { ... }
 */
export function profile(label: string) {
  return function (
    _target: unknown,
    _key: string | symbol,
    descriptor: PropertyDescriptor,
  ): PropertyDescriptor {
    const original = descriptor.value as (...args: unknown[]) => unknown;
    descriptor.value = function (this: unknown, ...args: unknown[]) {
      if (!frameProfiler.enabled || !_config.tracePhases) {
        return original.apply(this, args);
      }
      frameProfiler.begin(label);
      try {
        return original.apply(this, args);
      } finally {
        frameProfiler.end(label);
      }
    };
    return descriptor;
  };
}

/**
 * Entity metodu decorator'u: entity basina kirilim icin entities tablosuna yazar.
 * Etiket "<metodAdi>:<constructor.name>" olarak otomatik uretilir.
 * Profiler kapaliyken veya perEntity=false iken no-op.
 *
 * Kullanim:
 *   class SmoothOrbitController extends Entity {
 *     @profileEntity
 *     animate(dt, scene, camera) { ... }
 *
 *     @profileEntity
 *     beforeRender() { ... }
 *   }
 */
export function profileEntity(
  _target: unknown,
  key: string | symbol,
  descriptor: PropertyDescriptor,
): PropertyDescriptor {
  const original = descriptor.value as (...args: unknown[]) => unknown;
  const methodName = typeof key === "string" ? key : key.description ?? "method";
  descriptor.value = function (this: { constructor?: { name?: string } }, ...args: unknown[]) {
    if (!frameProfiler.enabled || !_config.perEntity) {
      return original.apply(this, args);
    }
    const label = methodName + ":" + (this.constructor?.name || "Unknown");
    const t0 = performance.now();
    try {
      return original.apply(this, args);
    } finally {
      frameProfiler.addEntitySample(label, performance.now() - t0);
    }
  };
  return descriptor;
}

declare global {
  interface Window {
    __engineProfiling?: typeof engineProfiling;
  }
}

if (typeof window !== "undefined") {
  window.__engineProfiling = engineProfiling;
}
